import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';

import {
  useTrackBrandContextSelected,
  useTrackCoverImageAdded,
  useTrackCustomLogoAdded,
  useTrackShareLinkFontUpdated,
} from '@air/analytics';
import { type Clip, type ShortUrlResponse } from '@air/api/types';
import { type SegmentedControlOption } from '@air/component-segmented-control';
import { useToasts } from '@air/provider-toast';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type DesignPreviewThumbnailRadius } from '~/components/ShareModal/components/designPreview/DesignPreviewThumbnailStrip';
import {
  type ShareCustomColors,
  type ShareDesignColorRole,
} from '~/components/ShareModal/components/ShareDesignColorRow';
import { type ShareDesignImageOption } from '~/components/ShareModal/components/ShareDesignImageButton';
import {
  type BrandColorEntry,
  type BrandContextHexByRole,
  type BrandFontEntry,
  DEFAULT_BUTTON_BG_HEX,
  DEFAULT_COLOR_MODE_OPTION,
  DEFAULT_HEADER_BG_HEX,
  DEFAULT_PAGE_BG_HEX,
  DEFAULT_PAGE_WIDTH_OPTION,
  DEFAULT_THUMBNAIL_RADIUS_OPTION,
  mapBrandColorEntryToColorRef,
  mapBrandColors,
  mapBrandColorsToRoles,
  mapBrandFonts,
  mapBrandImages,
  resolveColorRowProps,
  resolveRoleHex,
  SHARE_LINK_DESIGN_COLOR_MODE_OPTIONS,
  SHARE_LINK_DESIGN_PAGE_WIDTH_OPTIONS,
  SHARE_LINK_DESIGN_THUMBNAIL_RADIUS_OPTIONS,
  type TransientColors,
} from '~/components/ShareModal/shareLinkDesignResolution';
import { type CustomFontsInput, type CustomLogoInput } from '~/components/ShareModal/types/presentationSettingsInputs';
import { useUpdateShareBoardLink } from '~/components/SharePrivateBoardModal/hooks/useUpdateShareBoardLink';
import { COLOR_MODE_DEFAULT } from '~/components/v2/components/shared/share-link-design/shareLinkColorMode';
import {
  PAGE_WIDTH_DEFAULT,
  type ShareLinkPageWidth,
} from '~/components/v2/components/shared/share-link-design/shareLinkPageWidth';
import { THUMBNAIL_RADIUS_DEFAULT } from '~/components/v2/components/shared/share-link-design/shareLinkThumbnailRadius';
import { usePrivateBoardsDeleteBoardCoverMutation } from '~/components/v2/hooks/queries/mutations/usePrivateBoardsDeleteBoardCoverMutation';
import { usePrivateBoardsSetBoardCoverMutation } from '~/components/v2/hooks/queries/mutations/usePrivateBoardsSetBoardCoverMutation';
import { usePrivateBoard } from '~/components/v2/hooks/queries/usePrivateBoard';
import { usePrivateContextSourcesGet } from '~/components/v2/hooks/queries/usePrivateContextSourcesGet';
import { useShareSettings } from '~/hooks/useShareSettings';
import { useCurrentWorkspace } from '~/providers/CurrentWorkspaceProvider';
import { isDevOrTestStage } from '~/swr-hooks/utils';

const COLOR_ROLES: ShareDesignColorRole[] = [
  {
    analyticsRole: 'header_background',
    defaultHex: DEFAULT_HEADER_BG_HEX,
    label: 'Header background',
    readKey: 'headerBgColor',
    testId: 'SHARE_BOARD_DESIGN_COLOR_SWATCH_HEADER_BACKGROUND',
    writeKeys: ['headerBgColor'],
  },
  {
    analyticsRole: 'buttons_and_headlines',
    defaultHex: DEFAULT_BUTTON_BG_HEX,
    label: 'Buttons & headlines',
    readKey: 'buttonBgColor',
    testId: 'SHARE_BOARD_DESIGN_COLOR_SWATCH_BUTTONS_AND_HEADLINES',
    // One control authors both stored roles so public buttons and headlines always match.
    writeKeys: ['buttonBgColor', 'headlineBgColor'],
  },
  {
    analyticsRole: 'page_background',
    defaultHex: DEFAULT_PAGE_BG_HEX,
    label: 'Page background',
    readKey: 'pageBgColor',
    testId: 'SHARE_BOARD_DESIGN_COLOR_SWATCH_PAGE_BACKGROUND',
    writeKeys: ['pageBgColor'],
  },
];

/**
 * Full-frame default when setting a cover from a picked clip — same default as
 * `BoardCoverSelectModal`. Reposition/crop UX is a follow-up.
 */
const FULL_FRAME_COVER_REGION = { top: 0, left: 0, width: 100, height: 100 };

/** The built-in (`system`) headline font this tab authors: a family, plus its variant once known. */
type ShareBoardHeadlineFontSelection = {
  fontFamily: string;
  variant: string | undefined;
};

const areHeadlineFontSelectionsEqual = (
  first: ShareBoardHeadlineFontSelection | undefined,
  second: ShareBoardHeadlineFontSelection | undefined,
) => !!first && !!second && first.fontFamily === second.fontFamily && first.variant === second.variant;

export type ShareBoardDesignProviderProps = {
  boardId: string;
  shortIdInfo: ShortUrlResponse;
};

export type ShareBoardDesignContextValue = {
  /** Cosmetic preview title (the board's own title). */
  boardTitle: string;
  /** Current workspace logo URL (preview fallback identity), if any. */
  workspaceLogoSrc: string | undefined;
  /** Current workspace name (preview fallback identity). */
  workspaceName: string;

  /** All color roles the Colors section renders. */
  colorRoles: ShareDesignColorRole[];
  /**
   * Resolves a color row's display props (display hex + custom flag + backing brand value id) for
   * a given role, so the per-role color row components stay thin and don't recompute the
   * provider's resolution chain.
   */
  getColorRowProps: (role: ShareDesignColorRole) => {
    displayHex: string;
    isCustomColor: boolean;
    selectedBrandColorId: string | undefined;
  };

  /** Currently selected brand context source id (`undefined` => None). */
  selectedContextSourceId: string | undefined;
  /** Brand-kit image options for the LOGO picker dropdown (empty when no brand context selected). */
  logoBrandOptions: ShareDesignImageOption[];
  /** The brand value id backing the current logo, when the stored logo is a contextSource ref. */
  selectedLogoBrandOptionId: string | undefined;
  /**
   * Whether a custom logo VALUE exists (just-picked transient or stored ref), independent of
   * whether its preview URL has resolved yet — a just-committed brand ref has no `url` until the
   * save round-trips, and "Remove image" must gate on the value, not the preview.
   */
  hasStoredLogo: boolean;
  /** Brand-kit font entries for the font row's dropdown (empty when no brand context selected). */
  brandFonts: BrandFontEntry[];
  /** Brand-kit color entries for the color rows' swatches (empty when no brand context selected). */
  brandColors: BrandColorEntry[];
  /** The brand value id backing the current font, when the stored font is a contextSource ref. */
  selectedBrandFontId: string | undefined;

  /** Resolved hex for the page background (transient ?? stored ?? brand ?? default). */
  pageHex: string;
  /** Resolved hex for the header bar (transient ?? stored ?? brand ?? default). */
  headerHex: string;
  /** Resolved hex for the buttons & headlines accent (transient ?? stored ?? brand ?? default). */
  buttonHex: string;

  /** Resolved logo URL (just-picked transient ?? server-resolved). */
  logoSrc: string | undefined;
  /** Resolved cover URL (just-picked transient ?? the board's own resolved cover). */
  coverSrc: string | undefined;
  /** Resolved headline font family (brand entry ?? stored), if any. */
  fontFamily: string | undefined;
  /** Selected headline font variant, if any. */
  fontVariant: string | undefined;
  /**
   * Uploaded font file backing the current font (brand entry ?? stored resolved read shape), for
   * the preview's `@font-face` registration. Absent for built-in families — they load by name.
   */
  fontFaceUrl: string | undefined;

  /** Resolved preview theme/layout values (stored ?? default — the preview does no defaulting). */
  isDark: boolean;
  previewThumbnailRadius: DesignPreviewThumbnailRadius;
  previewPageWidth: ShareLinkPageWidth;

  /** Plan/in-flight gates the controls read. */
  brandingDisabled: boolean;
  coverDisabled: boolean;
  typographyDisabled: boolean;
  brandCustomizationEnabled: boolean;
  /** Read only by the cover row, the one control still on the broader paid design gate. */
  designSettingsEnabled: boolean;
  customTypographyEnabled: boolean;

  /** Segmented-control options (memoized with the disabled gate applied). */
  colorModeOptions: SegmentedControlOption[];
  thumbnailRadiusOptions: SegmentedControlOption[];
  pageWidthOptions: SegmentedControlOption[];
  /** Currently selected option per segmented control. */
  selectedColorModeOption: SegmentedControlOption;
  selectedThumbnailRadiusOption: SegmentedControlOption;
  selectedPageWidthOption: SegmentedControlOption;

  handleSelectBrandContext: (nextId: string | undefined) => void;
  handleSelectLogo: (clip: Clip) => void;
  handleClearLogo: () => void;
  handleSelectCover: (clip: Clip) => void;
  handleClearCover: () => void;
  handleClearTransientColor: (role: ShareDesignColorRole) => void;
  handlePreviewChange: (role: ShareDesignColorRole, hex: string) => void;
  handleCommitColor: (role: ShareDesignColorRole, hex: string | null) => void;
  handleCommitBrandColor: (role: ShareDesignColorRole, entry: BrandColorEntry) => void;
  handleResetColor: (role: ShareDesignColorRole) => void;
  handleChangeFont: (next: { fontFamily: string; variant?: string }) => void;
  /** Commits a family pick whose variant never arrived, on the font popover closing. */
  handleFontPopoverOpenChange: (isOpen: boolean) => void;
  handleClearFont: () => void;
  handleSelectBrandFont: (brandFont: BrandFontEntry) => void;
  handleSelectColorMode: (option: SegmentedControlOption) => void;
  handleSelectThumbnailRadius: (option: SegmentedControlOption) => void;
  handleSelectPageWidth: (option: SegmentedControlOption) => void;
};

const defaultContextValue: ShareBoardDesignContextValue = {
  boardTitle: '',
  workspaceLogoSrc: undefined,
  workspaceName: '',
  colorRoles: COLOR_ROLES,
  getColorRowProps: (role) => ({ displayHex: role.defaultHex, isCustomColor: false, selectedBrandColorId: undefined }),
  selectedContextSourceId: undefined,
  logoBrandOptions: [],
  selectedLogoBrandOptionId: undefined,
  hasStoredLogo: false,
  brandFonts: [],
  brandColors: [],
  selectedBrandFontId: undefined,
  pageHex: DEFAULT_PAGE_BG_HEX,
  headerHex: DEFAULT_HEADER_BG_HEX,
  buttonHex: DEFAULT_BUTTON_BG_HEX,
  logoSrc: undefined,
  coverSrc: undefined,
  fontFamily: undefined,
  fontVariant: undefined,
  fontFaceUrl: undefined,
  isDark: false,
  previewThumbnailRadius: THUMBNAIL_RADIUS_DEFAULT,
  previewPageWidth: PAGE_WIDTH_DEFAULT,
  brandingDisabled: false,
  coverDisabled: false,
  typographyDisabled: false,
  brandCustomizationEnabled: false,
  designSettingsEnabled: false,
  customTypographyEnabled: false,
  colorModeOptions: SHARE_LINK_DESIGN_COLOR_MODE_OPTIONS,
  thumbnailRadiusOptions: SHARE_LINK_DESIGN_THUMBNAIL_RADIUS_OPTIONS,
  pageWidthOptions: SHARE_LINK_DESIGN_PAGE_WIDTH_OPTIONS,
  // Derived from the value defaults (COLOR_MODE_DEFAULT/THUMBNAIL_RADIUS_DEFAULT/PAGE_WIDTH_DEFAULT
  // set above) so the seed's selected options can never drift from what those values actually mean.
  selectedColorModeOption: DEFAULT_COLOR_MODE_OPTION,
  selectedThumbnailRadiusOption: DEFAULT_THUMBNAIL_RADIUS_OPTION,
  selectedPageWidthOption: DEFAULT_PAGE_WIDTH_OPTION,
  handleSelectBrandContext: () => {},
  handleSelectLogo: () => {},
  handleClearLogo: () => {},
  handleSelectCover: () => {},
  handleClearCover: () => {},
  handleClearTransientColor: () => {},
  handlePreviewChange: () => {},
  handleCommitColor: () => {},
  handleCommitBrandColor: () => {},
  handleResetColor: () => {},
  handleChangeFont: () => {},
  handleFontPopoverOpenChange: () => {},
  handleClearFont: () => {},
  handleSelectBrandFont: () => {},
  handleSelectColorMode: () => {},
  handleSelectThumbnailRadius: () => {},
  handleSelectPageWidth: () => {},
};

export const ShareBoardDesignContext = createContext<ShareBoardDesignContextValue>(defaultContextValue);

export const useShareBoardDesign = () => {
  const ctx = useContext(ShareBoardDesignContext);

  if (ctx === defaultContextValue) {
    const error = new Error('useShareBoardDesign must be used within ShareBoardDesignProvider');

    if (isDevOrTestStage()) {
      throw error;
    }

    reportErrorToBugsnag({
      error,
      context: 'Share board design context not found',
    });
  }

  return ctx;
};

export const ShareBoardDesignProvider = ({
  boardId,
  shortIdInfo,
  children,
}: ShareBoardDesignProviderProps & { children: ReactNode }) => {
  const { currentWorkspace } = useCurrentWorkspace();
  // includeCoverImage: the API omits `coverImage` unless asked; the preview renders the board cover.
  const { data: board } = usePrivateBoard({ boardId, includeCoverImage: true });
  const { designSettingsEnabled, brandCustomizationEnabled, customTypographyEnabled } = useShareSettings();
  const { onShortUrlUpdate, isLoading } = useUpdateShareBoardLink({ boardId, shortIdInfo });
  const { showToast } = useToasts();
  const { trackBrandContextSelected } = useTrackBrandContextSelected();
  const { trackCoverImageAdded } = useTrackCoverImageAdded();
  const { trackCustomLogoAdded } = useTrackCustomLogoAdded();
  const { trackShareLinkFontUpdated } = useTrackShareLinkFontUpdated();
  /*
   * The board share-link backend rejects `presentationSettings.coverImage` on `objectType: 'board'`,
   * so "Choose cover" authors the BOARD'S OWN cover (the public board page renders it) through the
   * board cover mutations instead of the share-link update pipeline.
   */
  const { mutate: setBoardCover, isPending: isSettingCover } = usePrivateBoardsSetBoardCoverMutation();
  const { mutate: deleteBoardCover, isPending: isDeletingCover } = usePrivateBoardsDeleteBoardCoverMutation();

  const [transientColors, setTransientColors] = useState<TransientColors>({});
  /**
   * A freshly picked asset has no server-resolved `url` yet (the logo resolves on save round-trip;
   * the cover on the board refetch), so the just-picked thumbnail URL is tracked locally to drive
   * the preview until the stored value comes back with a `url`. Cleared when the image is removed.
   */
  const [transientLogoSrc, setTransientLogoSrc] = useState<string | undefined>(undefined);
  const [transientCoverSrc, setTransientCoverSrc] = useState<string | undefined>(undefined);
  /**
   * The built-in font the author is picking RIGHT NOW, held as React state rather than read back out
   * of the saved value. A family pick and its variant are two separate emits from the shared font
   * row (the variant select defaults itself once the family's metadata arrives), and the second emit
   * only knows the first happened if the row can already see it. On this surface the saved value
   * lives in the shortId REACT-QUERY CACHE, whose notification never reaches the row inside the
   * synchronous render cascade the first emit starts — so the variant select would keep seeing no
   * variant and re-emit until React aborted with "Maximum update depth exceeded" (FOUND-600). State
   * updates within that cascade, which is why the collection editor (a React-state draft) never had
   * the bug. Cleared whenever the font is replaced wholesale (brand pick / reset), and — for a pick
   * whose save failed — when the popover closes (see `failedHeadlineFontRef`).
   */
  const [localHeadlineFont, setLocalHeadlineFontState] = useState<ShareBoardHeadlineFontSelection | undefined>(
    undefined,
  );
  /**
   * Mirror of `localHeadlineFont` for reads that happen inside the same tick as a write. The font row
   * closes its popover synchronously right after calling the clear / brand-pick callbacks, so the
   * close handler below runs on the closure of the render that is already obsolete — where the state
   * still holds the pick those callbacks just replaced. Flushing from that stale value would re-save
   * the abandoned built-in font over the brand ref (or undo the clear). State stays the render input;
   * the ref is the imperative one.
   */
  const localHeadlineFontRef = useRef<ShareBoardHeadlineFontSelection | undefined>(undefined);
  /** Single writer for both, so the ref can never drift from the state it mirrors. */
  const setLocalHeadlineFont = useCallback((next: ShareBoardHeadlineFontSelection | undefined) => {
    localHeadlineFontRef.current = next;
    setLocalHeadlineFontState(next);
  }, []);
  /**
   * The last font payload sent, so a repeat of the same complete font never PATCHes twice — the
   * variant select re-emits its default on any render where the variant still looks absent.
   */
  const committedHeadlineFontRef = useRef<ShareBoardHeadlineFontSelection | undefined>(undefined);
  /**
   * The built-in pick whose save FAILED, so the popover-close handler can tell it from a saved one.
   *
   * A failed save KEEPS its pick on screen while the popover is open, which is the whole reason this
   * ref exists. Dropping the pick there would revert `fontVariant` to the rolled-back stored value —
   * usually absent, since a variant-less stored font is exactly what the heal path below exists for —
   * and `BuiltInFontVariantSelect` auto-defaults the variant again the moment it sees one missing.
   * That emit commits, fails, drops the pick, and emits again: an unbounded auto-retry against a
   * failing endpoint (FOUND-600's loop, on the error path). Keeping the pick leaves the select
   * looking at a complete, valid variant, so it cannot re-emit — the retry is bounded to one attempt
   * per authoring gesture, structurally, with no attempt counter to get wrong.
   *
   * UI truth is therefore DELIBERATE, in two steps: while the popover is open the row keeps showing
   * the failed pick and the error toast is the failure signal (the author can retry it straight
   * away); on close the pick is dropped back to stored truth, so the row never keeps advertising an
   * unsaved value. Closing unmounts the variant select, which is what makes that revert safe.
   */
  const failedHeadlineFontRef = useRef<ShareBoardHeadlineFontSelection | undefined>(undefined);
  /**
   * Whether the font popover is open RIGHT NOW, for the save-error handler below — a rejection can
   * land either side of the close, and which side it lands on decides whether keeping the pick is
   * protective or just stale. Written first thing in `handleFontPopoverOpenChange`, so the
   * close-flush's own rejection (started after that write) always reads the popover as closed.
   */
  const isFontPopoverOpenRef = useRef(false);

  // Brand context selection is not persisted; it's a transient seeding control.
  const [selectedContextSourceId, setSelectedContextSourceId] = useState<string | undefined>(undefined);
  /**
   * Hexes resolved from the selected brand context, keyed by role. Drives the preview/swatches for
   * the moment between applying a contextSource ref and the stored value coming back resolved.
   */
  const [contextHexByRole, setContextHexByRole] = useState<BrandContextHexByRole>({});
  // Guards the auto-map effect so a given selection applies exactly once (not on every color edit).
  const appliedSourceIdRef = useRef<string | undefined>(undefined);
  /**
   * Rehydration guards. The selection itself isn't persisted, but every stored contextSource ref
   * carries its owning kit id, so the selection is derived synchronously below and seeded once.
   * `hasUserPickedBrandContextRef` makes an explicit user pick (including "None") always beat
   * rehydration, and `hasRehydratedBrandContextRef` makes rehydration one-shot.
   */
  const hasUserPickedBrandContextRef = useRef(false);
  const hasRehydratedBrandContextRef = useRef(false);

  const storedColorMode = shortIdInfo.presentationSettings?.colorMode;
  const storedCustomColors = shortIdInfo.presentationSettings?.customColors;
  const storedCustomFonts = shortIdInfo.presentationSettings?.customFonts;
  const storedCustomLogo = shortIdInfo.presentationSettings?.customLogo;
  const storedPageWidth = shortIdInfo.presentationSettings?.pageWidth;
  const storedThumbnailRadius = shortIdInfo.presentationSettings?.thumbnailRadius;

  // The stored headlineFont is a write-capable union; `fontFamily`/`variant` exist on system/asset
  // and on the resolved contextSource read shape, so they're read via presence guards.
  const storedHeadlineFont = storedCustomFonts?.headlineFont;
  // The saved built-in font, in the same shape the row emits — the baseline every pick is compared
  // against, so re-committing a font that is already saved is impossible.
  const storedBuiltInHeadlineFont = useMemo<ShareBoardHeadlineFontSelection | undefined>(
    () =>
      storedHeadlineFont?.type === 'system'
        ? { fontFamily: storedHeadlineFont.fontFamily, variant: storedHeadlineFont.variant }
        : undefined,
    [storedHeadlineFont],
  );

  // Every stored brand-kit ref carries its owning kit id, so the rehydrated selection is derived
  // synchronously: first contextSource ref found wins (logo, then colors, then the headline font —
  // one kit authors them all, so they agree whenever more than one is stored).
  const storedContextSourceId = useMemo(() => {
    if (storedCustomLogo?.type === 'contextSource') {
      return storedCustomLogo.contextSourceId;
    }

    const storedColorValues = [
      storedCustomColors?.headerBgColor,
      storedCustomColors?.buttonBgColor,
      storedCustomColors?.headlineBgColor,
      storedCustomColors?.pageBgColor,
      storedCustomColors?.accentBgColor,
    ];

    for (const colorValue of storedColorValues) {
      if (colorValue?.type === 'contextSource') {
        return colorValue.contextSourceId;
      }
    }

    const storedHeadlineFont = storedCustomFonts?.headlineFont;

    // A font-only brand link (no brand logo/colors stored) must still rehydrate its kit selection.
    if (storedHeadlineFont?.type === 'contextSource') {
      return storedHeadlineFont.contextSourceId;
    }

    return undefined;
  }, [storedCustomColors, storedCustomFonts, storedCustomLogo]);

  useEffect(() => {
    // Rehydrate the brand-context selection from the stored refs at most once, and never over an
    // explicit user pick (including picking "None" before rehydration runs).
    if (
      !storedContextSourceId ||
      hasRehydratedBrandContextRef.current ||
      hasUserPickedBrandContextRef.current ||
      selectedContextSourceId
    ) {
      return;
    }

    hasRehydratedBrandContextRef.current = true;

    // CRITICAL: pre-seed the applied guard BEFORE selecting. The brand-color auto-apply effect
    // below fires a customColors PATCH whenever a NEWLY selected source's fields load; seeding
    // `appliedSourceIdRef` first makes it treat the rehydrated source as already applied, so
    // rehydration stays inert — it must never produce a PATCH on load.
    appliedSourceIdRef.current = storedContextSourceId;
    setSelectedContextSourceId(storedContextSourceId);
  }, [storedContextSourceId, selectedContextSourceId]);

  const { data: selectedSource } = usePrivateContextSourcesGet(
    { workspaceId: currentWorkspace?.id, contextSourceId: selectedContextSourceId },
    { enabled: !!selectedContextSourceId },
  );

  // The fetched source can outlive the selection (react-query keeps the last data once the query is
  // disabled), so brand values gate on the id matching the current selection — same guard as the
  // auto-map effect below.
  const activeBrandSource =
    selectedSource && selectedSource.id === selectedContextSourceId ? selectedSource : undefined;

  const brandColors = useMemo(() => (activeBrandSource ? mapBrandColors(activeBrandSource) : []), [activeBrandSource]);

  const brandFonts = useMemo<BrandFontEntry[]>(
    () => (activeBrandSource ? mapBrandFonts(activeBrandSource) : []),
    [activeBrandSource],
  );

  /*
   * Every `onShortUrlUpdate` call below attaches a catch: the promise otherwise floats, so a
   * rejected save (e.g. a backend validation failure on a brand-kit logo ref) escapes as an
   * unhandled runtime error. `useUpdateShortId` already rolls the optimistic cache write back and
   * reports to Bugsnag; the tab's job is the user-visible toast — same idiom as the cover
   * mutations' onError below.
   */
  const handleDesignSaveError = useCallback(() => {
    showToast('Failed to save design settings. Please try again.');
  }, [showToast]);

  const handleCustomLogoError = useCallback(() => {
    // Drop the optimistic preview so the tab doesn't keep showing a logo that never saved (the
    // share-link cache rollback restores the stored value underneath).
    setTransientLogoSrc(undefined);
    showToast('Failed to update logo. Please try again.');
  }, [showToast]);

  const handleCommitCustomLogo = useCallback(
    (customLogo: CustomLogoInput | null) => {
      // Clearing the logo is not an "added" event, so only a real logo is tracked. The stored shape
      // is what distinguishes the two authoring routes: a brand-kit pick commits a contextSource
      // reference, a picked asset commits an asset reference.
      if (customLogo) {
        trackCustomLogoAdded({
          from_brand_context: customLogo.type === 'contextSource',
          share_link_type: 'board',
        });
      }

      onShortUrlUpdate({ props: { presentationSettings: { customLogo } } }).catch(handleCustomLogoError);
    },
    [handleCustomLogoError, onShortUrlUpdate, trackCustomLogoAdded],
  );

  const logoBrandOptions = useMemo<ShareDesignImageOption[]>(() => {
    if (!activeBrandSource) {
      return [];
    }

    return mapBrandImages(activeBrandSource).map((entry) => ({
      id: entry.id,
      fieldName: entry.fieldName,
      imageSrc: entry.url,
      label: entry.label,
      onSelect: () => {
        // Same transient-preview pattern as handleSelectLogo: the brand value's URL drives the
        // preview until the stored contextSource ref round-trips with its server-resolved `url`.
        setTransientLogoSrc(entry.url);
        handleCommitCustomLogo({
          type: 'contextSource',
          contextSourceId: entry.contextSourceId,
          contextSourceFieldId: entry.contextSourceFieldId,
          contextSourceFieldValueId: entry.id,
        });
      },
    }));
  }, [activeBrandSource, handleCommitCustomLogo]);

  const handleSelectLogo = useCallback(
    (clip: Clip) => {
      setTransientLogoSrc(clip.assets.image);
      handleCommitCustomLogo({ type: 'asset', assetId: clip.assetId, versionId: clip.id });
    },
    [handleCommitCustomLogo],
  );

  const handleClearLogo = useCallback(() => {
    setTransientLogoSrc(undefined);
    handleCommitCustomLogo(null);
  }, [handleCommitCustomLogo]);

  const handleSelectCover = useCallback(
    (clip: Clip) => {
      const workspaceId = currentWorkspace?.id;

      if (!workspaceId) {
        return;
      }

      trackCoverImageAdded({ type: 'board' });

      setTransientCoverSrc(clip.assets.image);
      setBoardCover(
        {
          boardId,
          workspaceId,
          clipId: clip.id,
          thumbnailUrl: clip.assets.image,
          region: FULL_FRAME_COVER_REGION,
          // Passing the fetched board lets the mutation optimistically patch the board caches
          // (including this tab's usePrivateBoard read), so the app-wide board UIs update too.
          board,
        },
        {
          onError: () => {
            // Drop the optimistic preview so the tab doesn't keep showing a cover that never saved.
            setTransientCoverSrc(undefined);
            showToast('Failed to set board cover. Please try again.');
          },
        },
      );
    },
    [board, boardId, currentWorkspace?.id, setBoardCover, showToast, trackCoverImageAdded],
  );

  const handleClearCover = useCallback(() => {
    const workspaceId = currentWorkspace?.id;

    // The delete mutation requires the full board for its optimistic cache update. The board query
    // has normally resolved by the time a stored cover can be cleared (the preview shows its URL),
    // but if it hasn't (or the workspace is briefly unknown), surface the same failure the
    // mutation's onError would — silently swallowing the click strands a cover the user removed.
    if (!workspaceId || !board) {
      showToast('Failed to remove board cover. Please try again.');
      return;
    }

    setTransientCoverSrc(undefined);
    deleteBoardCover(
      { boardId, workspaceId, board },
      {
        onError: () => {
          showToast('Failed to remove board cover. Please try again.');
        },
      },
    );
  }, [board, boardId, currentWorkspace?.id, deleteBoardCover, showToast]);

  const handleClearTransientColor = useCallback((role: ShareDesignColorRole) => {
    setTransientColors((previous) => {
      const next = { ...previous };
      delete next[role.readKey];
      return next;
    });
  }, []);

  const handlePreviewChange = useCallback((role: ShareDesignColorRole, hex: string) => {
    setTransientColors((previous) => ({ ...previous, [role.readKey]: hex }));
  }, []);

  const handleCommitColor = useCallback(
    (role: ShareDesignColorRole, hex: string | null) => {
      /**
       * The update path wholesale-replaces `presentationSettings.customColors`, so the FULL desired
       * object is sent every time: stored roles this UI doesn't author (e.g. accentBgColor) are
       * carried over, and a reset role is cleared by omitting its key.
       */
      const nextCustomColors: ShareCustomColors = { ...storedCustomColors };

      for (const writeKey of role.writeKeys) {
        if (hex === null) {
          delete nextCustomColors[writeKey];
        } else {
          nextCustomColors[writeKey] = { type: 'hex', hex };
        }
      }

      handleClearTransientColor(role);

      onShortUrlUpdate({
        props: { presentationSettings: { customColors: nextCustomColors } },
        analytics: { updated_setting: 'custom_colors', color_role: role.analyticsRole, color_value: hex },
      }).catch(handleDesignSaveError);
    },
    [handleClearTransientColor, handleDesignSaveError, onShortUrlUpdate, storedCustomColors],
  );

  const handleCommitBrandColor = useCallback(
    (role: ShareDesignColorRole, entry: BrandColorEntry) => {
      // Full-replacement customColors, same contract as handleCommitColor above. The picked entry
      // commits as a contextSource REFERENCE on EVERY writeKey of the role (the buttons row
      // authors buttonBgColor + headlineBgColor with one ref, matching the auto-map's mirroring) —
      // the stored ref is what rehydrates the kit selection and swatch label on reload; a plain
      // hex would leave nothing to rehydrate from. The ref's `hex` previews instantly via the
      // optimistic cache write; the update normalizer strips it on the wire.
      const nextCustomColors: ShareCustomColors = { ...storedCustomColors };
      const brandColorRef = mapBrandColorEntryToColorRef(entry);

      for (const writeKey of role.writeKeys) {
        nextCustomColors[writeKey] = brandColorRef;
      }

      handleClearTransientColor(role);

      onShortUrlUpdate({
        props: { presentationSettings: { customColors: nextCustomColors } },
        analytics: { updated_setting: 'custom_colors', color_role: role.analyticsRole, color_value: entry.hex },
      }).catch(handleDesignSaveError);
    },
    [handleClearTransientColor, handleDesignSaveError, onShortUrlUpdate, storedCustomColors],
  );

  const handleResetColor = useCallback(
    (role: ShareDesignColorRole) => handleCommitColor(role, null),
    [handleCommitColor],
  );

  /*
   * The narrowing deliberately excludes the wire union's 'toggle' member: the authoring options only
   * offer Light/Dark (a viewer-facing theme toggle is a named follow-up), so 'toggle' can never be
   * committed from here — mirrors the collection design provider. Re-selecting the already-selected
   * option never reaches this handler (the SegmentedControl swallows it), so a legacy link showing
   * the Light default is never silently upgraded from "visitor's choice" to a forced light theme.
   */
  const handleSelectColorMode = useCallback(
    (option: SegmentedControlOption) => {
      if (option.value === 'light' || option.value === 'dark') {
        onShortUrlUpdate({
          props: { presentationSettings: { colorMode: option.value } },
          analytics: { updated_setting: 'page_theme', page_theme: option.value },
        }).catch(handleDesignSaveError);
      }
    },
    [handleDesignSaveError, onShortUrlUpdate],
  );

  /*
   * The corner-rounding options mirror the wire union verbatim, so unlike colorMode this narrowing
   * exists only to type `option.value` (a plain string on SegmentedControlOption) without an
   * assertion. Re-selecting the already-selected option never reaches this handler (the
   * SegmentedControl swallows it), so a legacy link showing the Standard default is never silently
   * rewritten to a stored 'standard'.
   */
  const handleSelectThumbnailRadius = useCallback(
    (option: SegmentedControlOption) => {
      if (option.value === 'standard' || option.value === 'subtle' || option.value === 'square') {
        onShortUrlUpdate({
          props: { presentationSettings: { thumbnailRadius: option.value } },
          analytics: { updated_setting: 'corner_rounding', corner_rounding: option.value },
        }).catch(handleDesignSaveError);
      }
    },
    [handleDesignSaveError, onShortUrlUpdate],
  );

  const handleFontSaveError = useCallback(() => {
    // Drop the commit baseline so an identical retry can send again — the shortId cache rollback
    // restores the stored value, and the in-progress pick deliberately STAYS on screen (see
    // `failedHeadlineFontRef`). The toast is the failure signal.
    committedHeadlineFontRef.current = undefined;
    handleDesignSaveError();
  }, [handleDesignSaveError]);

  /**
   * Where a failed built-in save leaves the row, which depends on whether the author is still
   * looking at the pick:
   *
   * - popover OPEN: keep the pick and mark it failed, so the variant select keeps seeing a complete
   *   variant (the loop-break — see `failedHeadlineFontRef`) and the author can retry it straight
   *   away against the toast. The close reverts it to stored truth.
   * - popover CLOSED: revert immediately. The variant select is unmounted, so nothing can
   *   auto-default its way into a retry, and nothing is showing the pick either — a rejection that
   *   lands after the close (including the close-flush's own) would otherwise leave the row
   *   advertising a font the server rolled back until the tab remounted.
   */
  const handleBuiltInFontSaveError = useCallback(
    (selection: ShareBoardHeadlineFontSelection) => {
      if (isFontPopoverOpenRef.current) {
        failedHeadlineFontRef.current = selection;
      } else {
        failedHeadlineFontRef.current = undefined;
        setLocalHeadlineFont(undefined);
      }

      handleFontSaveError();
    },
    [handleFontSaveError, setLocalHeadlineFont],
  );

  const handleCommitCustomFonts = useCallback(
    (customFonts: CustomFontsInput | null, onError: () => void) => {
      // @TODO(COR-1951): Later pass — emit a dedicated `updated_setting: 'custom_typography'`
      // analytics event; mirrors the collection edit modal, which sends none yet either.
      onShortUrlUpdate({ props: { presentationSettings: { customFonts } } }).catch(onError);
    },
    [onShortUrlUpdate],
  );

  const handleCommitBuiltInFont = useCallback(
    (selection: ShareBoardHeadlineFontSelection) => {
      if (
        areHeadlineFontSelectionsEqual(selection, storedBuiltInHeadlineFont) ||
        areHeadlineFontSelectionsEqual(selection, committedHeadlineFontRef.current)
      ) {
        return;
      }

      committedHeadlineFontRef.current = selection;
      // This attempt supersedes any earlier failure: whatever the author picked before is no longer
      // what the row is showing, so there's nothing left for the close to revert to stored truth.
      failedHeadlineFontRef.current = undefined;

      trackShareLinkFontUpdated({ font: selection.fontFamily, from_brand_context: false });

      // Built-in fonts only; brand-kit (`contextSource`) fonts commit via handleSelectBrandFont
      // below, and asset-font upload remains deferred (see the row's TODO).
      handleCommitCustomFonts(
        {
          headlineFont: {
            type: 'system',
            fontFamily: selection.fontFamily,
            ...(selection.variant ? { variant: selection.variant } : {}),
          },
        },
        () => handleBuiltInFontSaveError(selection),
      );
    },
    [handleBuiltInFontSaveError, handleCommitCustomFonts, storedBuiltInHeadlineFont, trackShareLinkFontUpdated],
  );

  const handleChangeFont = useCallback(
    (next: { fontFamily: string; variant?: string }) => {
      const nextSelection: ShareBoardHeadlineFontSelection = { fontFamily: next.fontFamily, variant: next.variant };

      setLocalHeadlineFont(nextSelection);

      // A family with no variant yet is half a font: the variant select completes it as soon as the
      // family's metadata arrives, so the whole pick commits on that second emit instead of saving a
      // variant-less font first. The popover-close handler below covers a pick abandoned before then.
      if (!nextSelection.variant) {
        return;
      }

      handleCommitBuiltInFont(nextSelection);
    },
    [handleCommitBuiltInFont, setLocalHeadlineFont],
  );

  const handleFontPopoverOpenChange = useCallback(
    (isOpen: boolean) => {
      // Recorded BEFORE anything else (in both directions): the flush below starts a save whose
      // rejection must read the popover as already closed, so the error handler reverts the pick
      // instead of keeping it for a variant select that no longer exists.
      isFontPopoverOpenRef.current = isOpen;

      // Closing on a family whose variant never arrived commits the family alone — a lost pick is
      // worse than a variant-less one, and reopening the popover heals it into a complete font. Read
      // through the ref: reset / brand-pick clear the pending pick and close the popover in one tick,
      // and the state this closure captured would still show the pick they just discarded.
      const pendingFont = localHeadlineFontRef.current;

      if (isOpen || !pendingFont) {
        return;
      }

      // A pick whose save FAILED goes back to stored truth here — the deliberate moment for that
      // revert, because closing unmounts the variant select that would otherwise auto-default its way
      // into an unbounded retry (see `failedHeadlineFontRef`). It must never reach the flush below:
      // that exists only for a family-only pick whose variant never arrived, not to resend a failure.
      if (areHeadlineFontSelectionsEqual(pendingFont, failedHeadlineFontRef.current)) {
        failedHeadlineFontRef.current = undefined;
        setLocalHeadlineFont(undefined);
        return;
      }

      if (pendingFont.variant) {
        return;
      }

      handleCommitBuiltInFont(pendingFont);
    },
    [handleCommitBuiltInFont, setLocalHeadlineFont],
  );

  const handleClearFont = useCallback(() => {
    setLocalHeadlineFont(undefined);
    committedHeadlineFontRef.current = undefined;
    failedHeadlineFontRef.current = undefined;
    handleCommitCustomFonts(null, handleFontSaveError);
  }, [handleCommitCustomFonts, handleFontSaveError, setLocalHeadlineFont]);

  const handleSelectBrandFont = useCallback(
    (brandFont: BrandFontEntry) => {
      // The brand ref replaces the built-in selection wholesale, so the local pick must go — the row
      // would otherwise keep rendering the built-in family over the brand font.
      setLocalHeadlineFont(undefined);
      committedHeadlineFontRef.current = undefined;
      failedHeadlineFontRef.current = undefined;

      trackShareLinkFontUpdated({ font: brandFont.family, from_brand_context: true });

      // A pure brand-kit ref — the server resolves fontFamily/url on read. The display family comes
      // from the brand entry lookup below until then.
      handleCommitCustomFonts(
        {
          headlineFont: {
            type: 'contextSource',
            contextSourceId: brandFont.contextSourceId,
            contextSourceFieldId: brandFont.contextSourceFieldId,
            contextSourceFieldValueId: brandFont.id,
          },
        },
        handleFontSaveError,
      );
    },
    [handleCommitCustomFonts, handleFontSaveError, setLocalHeadlineFont, trackShareLinkFontUpdated],
  );

  // Same shape as corner rounding above: verbatim wire-union options, assertion-free narrowing,
  // re-select swallowed by the SegmentedControl so a legacy link's Wide default is never rewritten.
  const handleSelectPageWidth = useCallback(
    (option: SegmentedControlOption) => {
      if (option.value === 'narrow' || option.value === 'standard' || option.value === 'wide') {
        onShortUrlUpdate({
          props: { presentationSettings: { pageWidth: option.value } },
          analytics: { updated_setting: 'page_width', page_width: option.value },
        }).catch(handleDesignSaveError);
      }
    },
    [handleDesignSaveError, onShortUrlUpdate],
  );

  const handleSelectBrandContext = useCallback(
    (nextId: string | undefined) => {
      // An explicit pick (either direction) permanently wins over any in-flight rehydration.
      hasUserPickedBrandContextRef.current = true;

      if (!nextId) {
        // Clearing the brand context resets the auto-mapped roles back to default by omitting their
        // keys, while preserving roles the map never touches (e.g. accentBgColor). The mapping
        // itself runs async, so there's no source to wait on here.
        setSelectedContextSourceId(undefined);
        setContextHexByRole({});
        appliedSourceIdRef.current = undefined;

        const nextCustomColors: ShareCustomColors = { ...storedCustomColors };
        delete nextCustomColors.headerBgColor;
        delete nextCustomColors.buttonBgColor;
        delete nextCustomColors.headlineBgColor;
        delete nextCustomColors.pageBgColor;

        onShortUrlUpdate({ props: { presentationSettings: { customColors: nextCustomColors } } }).catch(
          handleDesignSaveError,
        );
        return;
      }

      // The actual color mapping happens in the effect below, once the source's fields load.
      setSelectedContextSourceId(nextId);
    },
    [handleDesignSaveError, onShortUrlUpdate, storedCustomColors],
  );

  useEffect(() => {
    // Apply once per selection: wait for the fetched source to match the selected id, and skip if
    // we've already applied it (so manual color edits afterward don't re-trigger the brand apply).
    if (
      !selectedSource ||
      selectedSource.id !== selectedContextSourceId ||
      appliedSourceIdRef.current === selectedSource.id
    ) {
      return;
    }

    appliedSourceIdRef.current = selectedSource.id;

    const { nextRoles, contextHexByRole: hexByRole } = mapBrandColorsToRoles(selectedSource);

    setContextHexByRole(hexByRole);

    // Spread current colors so roles the map doesn't match (and untouched roles like accentBgColor)
    // are preserved, then overlay the matched contextSource refs.
    const nextCustomColors: ShareCustomColors = { ...storedCustomColors, ...nextRoles };

    // Tracked here rather than in the picker handler because the kit's NAME only exists once the
    // source has been fetched, and this apply is what actually changes the design. The apply is one
    // authoring action, so it reports as one brand_context_selected rather than a per-role color
    // event for each role the map overwrote.
    trackBrandContextSelected({ brand_context_name: selectedSource.name, share_link_type: 'board' });

    onShortUrlUpdate({ props: { presentationSettings: { customColors: nextCustomColors } } }).catch(
      handleDesignSaveError,
    );
  }, [
    selectedSource,
    selectedContextSourceId,
    storedCustomColors,
    onShortUrlUpdate,
    handleDesignSaveError,
    trackBrandContextSelected,
  ]);

  const headerHex = resolveRoleHex(
    'headerBgColor',
    transientColors,
    storedCustomColors,
    contextHexByRole,
    DEFAULT_HEADER_BG_HEX,
  );
  const buttonHex = resolveRoleHex(
    'buttonBgColor',
    transientColors,
    storedCustomColors,
    contextHexByRole,
    DEFAULT_BUTTON_BG_HEX,
  );
  const pageHex = resolveRoleHex(
    'pageBgColor',
    transientColors,
    storedCustomColors,
    contextHexByRole,
    DEFAULT_PAGE_BG_HEX,
  );

  // Display-only: lets the logo picker show the brand entry's label (and mark it selected) when the
  // stored logo is a brand-kit ref.
  const selectedLogoBrandOptionId =
    storedCustomLogo?.type === 'contextSource' ? storedCustomLogo.contextSourceFieldValueId : undefined;
  // A just-committed brand logo is a pure ref (no `url` until the save round-trips resolved), so
  // when the transient preview is gone (e.g. the tab remounted mid-save) the brand entry's URL
  // wins over the not-yet-resolved stored ref — mirrors the brand font's family fallback below.
  const brandLogoSrc = selectedLogoBrandOptionId
    ? logoBrandOptions.find((option) => option.id === selectedLogoBrandOptionId)?.imageSrc
    : undefined;
  // Just-picked transient URL wins until the stored value comes back resolved with its own `url`.
  const logoSrc = transientLogoSrc ?? storedCustomLogo?.url ?? brandLogoSrc;
  const coverSrc = transientCoverSrc ?? board?.coverImage?.url;
  // "Remove image" (and the dropdown itself) must gate on a VALUE existing, not on a resolved
  // preview URL — a just-committed brand ref can be stored with no `url` yet.
  const hasStoredLogo = !!transientLogoSrc || !!storedCustomLogo;

  // A local pick outranks the stored font on every font read below: it's the newer authored value,
  // and it's always a built-in one, so it also cancels any brand identity the stored font carried.
  const selectedBrandFontId =
    !localHeadlineFont && storedHeadlineFont?.type === 'contextSource'
      ? storedHeadlineFont.contextSourceFieldValueId
      : undefined;
  const storedFontFamily =
    storedHeadlineFont && 'fontFamily' in storedHeadlineFont ? storedHeadlineFont.fontFamily : undefined;
  // A just-committed brand font is a pure ref (no fontFamily until the save round-trips resolved),
  // so for contextSource refs the brand entry's family wins, falling back to the resolved read shape
  // (e.g. a saved brand font reopened with no brand context selected this session).
  const selectedBrandFont = selectedBrandFontId
    ? brandFonts.find((brandFont) => brandFont.id === selectedBrandFontId)
    : undefined;
  const resolvedStoredFontFamily = selectedBrandFontId
    ? (selectedBrandFont?.family ?? storedFontFamily)
    : storedFontFamily;
  const fontFamily = localHeadlineFont?.fontFamily ?? resolvedStoredFontFamily;
  const storedFontVariant =
    storedHeadlineFont && 'variant' in storedHeadlineFont ? storedHeadlineFont.variant : undefined;
  const fontVariant = localHeadlineFont ? localHeadlineFont.variant : storedFontVariant;
  // Uploaded font files must be registered via @font-face to preview — brand entry (fresh) wins
  // over the stored resolved read shape (e.g. a saved upload reopened without its kit loaded). A
  // local pick is always a built-in family, which loads by name, so it registers nothing.
  const storedFontFaceUrl =
    storedHeadlineFont && storedHeadlineFont.type !== 'system' && 'url' in storedHeadlineFont
      ? storedHeadlineFont.url
      : undefined;
  const fontFaceUrl = localHeadlineFont ? undefined : (selectedBrandFont?.fileUrl ?? storedFontFaceUrl);

  /*
   * Disable while a save is in flight: each share-link save sends a full-replacement customColors
   * object, so overlapping saves could clobber each other (out-of-order responses wholesale-replace
   * the stored colors). Serializing saves avoids that. The cover rides its own board mutations, so
   * it gates on those instead of the share-link save.
   */
  const brandingDisabled = !brandCustomizationEnabled || isLoading;
  // The board cover keeps the broader paid design gate. #8805 un-gated the SHOWCASE cover on every
  // plan and deliberately left board covers alone, so pulling this one up to Business+ would tighten
  // a surface no product decision touched.
  const coverDisabled = !designSettingsEnabled || isSettingCover || isDeletingCover;
  // Layout (page width, corner rounding) rides the broader design-settings gate — it's available on
  // any paid plan, while the brand elements above need Business+.
  const layoutDisabled = !designSettingsEnabled || isLoading;
  // The font row rides its OWN plan gate (typography, Enterprise-only), not the gates the other rows
  // share — mirrors the collection's font-row binding.
  const typographyDisabled = !customTypographyEnabled || isLoading;

  /*
   * Plan/in-flight gating rides on each option's `disabled` (`SegmentedControlRow` has no disabled
   * or upsell affordance of its own) — same idiom as the collection design provider's option
   * mapping, and the same reason the row shows no upsell popover like its color-row siblings do.
   */
  const colorModeOptions = useMemo(
    () => SHARE_LINK_DESIGN_COLOR_MODE_OPTIONS.map((option) => ({ ...option, disabled: brandingDisabled })),
    [brandingDisabled],
  );
  // Absent colorMode (legacy link) shows the Light default. The `?? [0]` arm is reachable: a stored
  // 'toggle' (wire union member with no authoring option) degrades to showing Light selected.
  const selectedColorModeOption =
    colorModeOptions.find((option) => option.value === (storedColorMode ?? COLOR_MODE_DEFAULT)) ?? colorModeOptions[0];

  const thumbnailRadiusOptions = useMemo(
    () => SHARE_LINK_DESIGN_THUMBNAIL_RADIUS_OPTIONS.map((option) => ({ ...option, disabled: layoutDisabled })),
    [layoutDisabled],
  );
  // Absent thumbnailRadius (legacy link) shows the Standard default — the same fallback the public
  // renderers apply, so the editor never disagrees with what the link actually renders. The `?? [0]`
  // arm only guards a wire value newer than the compiled union.
  const selectedThumbnailRadiusOption =
    thumbnailRadiusOptions.find((option) => option.value === (storedThumbnailRadius ?? THUMBNAIL_RADIUS_DEFAULT)) ??
    thumbnailRadiusOptions[0];

  const pageWidthOptions = useMemo(
    () => SHARE_LINK_DESIGN_PAGE_WIDTH_OPTIONS.map((option) => ({ ...option, disabled: layoutDisabled })),
    [layoutDisabled],
  );
  // Absent pageWidth (legacy link) shows the Wide default — same reasoning as thumbnailRadius above.
  const selectedPageWidthOption =
    pageWidthOptions.find((option) => option.value === (storedPageWidth ?? PAGE_WIDTH_DEFAULT)) ?? pageWidthOptions[0];

  // The optimistic cache write in useUpdateShortId lands the picked colorMode in `shortIdInfo`
  // immediately, so the preview flips themes on click — no transient state needed. A stored
  // 'toggle' previews as light, matching the public renderer's "visitor's preference" degradation.
  const isDark = storedColorMode === 'dark';
  // Same optimistic-cache reliance for the layout knobs: the preview re-renders on click with no
  // transient state. Resolved here (stored ?? default) because the preview does no defaulting.
  const previewThumbnailRadius = storedThumbnailRadius ?? THUMBNAIL_RADIUS_DEFAULT;
  const previewPageWidth = storedPageWidth ?? PAGE_WIDTH_DEFAULT;

  const getColorRowProps = useCallback(
    (role: ShareDesignColorRole) =>
      resolveColorRowProps({ role, transientColors, customColors: storedCustomColors, contextHexByRole }),
    [transientColors, storedCustomColors, contextHexByRole],
  );

  const contextValue = useMemo<ShareBoardDesignContextValue>(
    () => ({
      boardTitle: board?.title ?? '',
      workspaceLogoSrc: currentWorkspace?.image ?? undefined,
      workspaceName: currentWorkspace?.name ?? '',
      colorRoles: COLOR_ROLES,
      getColorRowProps,
      selectedContextSourceId,
      logoBrandOptions,
      selectedLogoBrandOptionId,
      hasStoredLogo,
      brandFonts,
      brandColors,
      selectedBrandFontId,
      pageHex,
      headerHex,
      buttonHex,
      logoSrc,
      coverSrc,
      fontFamily,
      fontVariant,
      fontFaceUrl,
      isDark,
      previewThumbnailRadius,
      previewPageWidth,
      brandingDisabled,
      coverDisabled,
      typographyDisabled,
      brandCustomizationEnabled,
      designSettingsEnabled,
      customTypographyEnabled,
      colorModeOptions,
      thumbnailRadiusOptions,
      pageWidthOptions,
      selectedColorModeOption,
      selectedThumbnailRadiusOption,
      selectedPageWidthOption,
      handleSelectBrandContext,
      handleSelectLogo,
      handleClearLogo,
      handleSelectCover,
      handleClearCover,
      handleClearTransientColor,
      handlePreviewChange,
      handleCommitColor,
      handleCommitBrandColor,
      handleResetColor,
      handleChangeFont,
      handleFontPopoverOpenChange,
      handleClearFont,
      handleSelectBrandFont,
      handleSelectColorMode,
      handleSelectThumbnailRadius,
      handleSelectPageWidth,
    }),
    [
      board?.title,
      currentWorkspace?.image,
      currentWorkspace?.name,
      getColorRowProps,
      selectedContextSourceId,
      logoBrandOptions,
      selectedLogoBrandOptionId,
      hasStoredLogo,
      brandFonts,
      brandColors,
      selectedBrandFontId,
      pageHex,
      headerHex,
      buttonHex,
      logoSrc,
      coverSrc,
      fontFamily,
      fontVariant,
      fontFaceUrl,
      isDark,
      previewThumbnailRadius,
      previewPageWidth,
      brandingDisabled,
      coverDisabled,
      typographyDisabled,
      brandCustomizationEnabled,
      designSettingsEnabled,
      customTypographyEnabled,
      colorModeOptions,
      thumbnailRadiusOptions,
      pageWidthOptions,
      selectedColorModeOption,
      selectedThumbnailRadiusOption,
      selectedPageWidthOption,
      handleSelectBrandContext,
      handleSelectLogo,
      handleClearLogo,
      handleSelectCover,
      handleClearCover,
      handleClearTransientColor,
      handlePreviewChange,
      handleCommitColor,
      handleCommitBrandColor,
      handleResetColor,
      handleChangeFont,
      handleFontPopoverOpenChange,
      handleClearFont,
      handleSelectBrandFont,
      handleSelectColorMode,
      handleSelectThumbnailRadius,
      handleSelectPageWidth,
    ],
  );

  return <ShareBoardDesignContext.Provider value={contextValue}>{children}</ShareBoardDesignContext.Provider>;
};

ShareBoardDesignProvider.displayName = 'ShareBoardDesignProvider';
